/**
 * New / edit labor rate — Board 1's second form (`catalog-units-and-buying.md` §6.1).
 *
 * It was the generic catalog sheet: one column of boxes, no sections, no
 * Readout, and a crew size beside a cost per crew hour that somebody worked
 * out in their head. Now it is the Materials form's anatomy with Labor's
 * words: **Paid by** a person hour **at** its loaded cost, **Allocated in** a
 * crew hour **=** so many people, the green sentence saying what that comes
 * to, then Priced by and the Readout. No buying rule and no Try it — labor is
 * always costed on its hours (D4).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { laborSentence, marginPctOf, type OrgUnitSummary } from '@rowted/shared'
import { Band } from '../primitives.js'
import {
  Field, FormGrid, FormSection, MoneyInput, NumberInput, PairField, Readout, TextInput, Wide,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { formatRate } from '../../lib/format.js'
import {
  CatalogSheet, CustomerNameField, NotesField, PricedByField, UnitSelect, WhereAndOptions,
  amountText, marginTone, percentText, readAmount, unitNamed, type UnitOption,
} from './catalog-parts.js'
import { createUnit, useCatalogLookups } from './catalog-lookups.js'
import {
  ALLOCATED_IN, BAND_HINT, BUY_LABEL, DESCRIPTION, MARKUP_ON_COST, NAME, PEOPLE, WHAT_IT_COSTS,
  WHAT_IT_COSTS_SUB, WHAT_IT_IS, WHAT_IT_SELLS_FOR, costPerLabel, costSlash, perLabel, pricePer,
  sellsSlash,
} from './catalog-copy.js'

interface LaborDraft {
  name: string
  /** Customer-facing name (0126); blank is "the same as the name". */
  customerName: string
  description: string
  purchaseUnitId: string | null
  unitId: string | null
  personHourCost: string
  crewSize: string
  pricing: 'markup' | 'fixed'
  markupPct: string
  billedRate: string
  taxable: boolean
  orgUnitIds: string[]
  notes: string
  /* Carried through untouched: Board 1 does not draw them, so a save keeps them. */
  active: boolean
  version?: number
}

const BLANK: LaborDraft = {
  name: '', customerName: '', description: '', purchaseUnitId: null, unitId: null, personHourCost: '',
  crewSize: '1', pricing: 'fixed', markupPct: '', billedRate: '', taxable: false,
  orgUnitIds: [], notes: '', active: true,
}

export function LaborForm({ id, canEdit, available, onClose, onSaved }: {
  id: string | null
  canEdit: boolean
  available: OrgUnitSummary[]
  onClose: () => void
  onSaved: (message: string, again: boolean) => void
}) {
  const lookups = useCatalogLookups()
  const [draft, setDraft] = useState<LaborDraft | null>(id ? null : { ...BLANK })
  const [saved, setSaved] = useState<LaborDraft | null>(id ? null : { ...BLANK })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const body = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!id) return
    try {
      const row = await request<Record<string, unknown>>(`/catalog-admin/labor-rates/${id}`)
      const loaded: LaborDraft = {
        name: String(row.name ?? ''),
        customerName: String(row.customerName ?? ''),
        description: String(row.description ?? ''),
        purchaseUnitId: (row.purchaseUnitId as string | null) ?? null,
        unitId: (row.unitId as string | null) ?? null,
        personHourCost: amountText(row.personHourCost, true),
        crewSize: String(row.crewSize ?? 1),
        pricing: row.markupPct === null || row.markupPct === undefined ? 'fixed' : 'markup',
        markupPct: row.markupPct === null || row.markupPct === undefined ? '' : String(Number(row.markupPct)),
        billedRate: amountText(row.billedRate, true),
        taxable: row.taxable === true,
        orgUnitIds: (row.orgUnitIds as string[] | undefined) ?? [],
        notes: String(row.notes ?? ''),
        active: row.active !== false,
        version: Number(row.version ?? 0),
      }
      setDraft(loaded)
      setSaved(loaded)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That labor rate could not be loaded.')
    }
  }, [id])
  useEffect(() => { void load() }, [load])

  /* A new rate starts in Person hour and Crew hour, the two 0118 seeded. */
  useEffect(() => {
    if (id || !draft || lookups.units.length === 0 || draft.unitId) return
    const ph = lookups.units.find((unit) => unit.code === 'ph')?.id ?? null
    const ch = lookups.units.find((unit) => unit.code === 'ch')?.id ?? null
    setDraft({ ...draft, purchaseUnitId: ph, unitId: ch })
    setSaved((was) => (was ? { ...was, purchaseUnitId: ph, unitId: ch } : was))
  }, [id, draft, lookups.units])

  const patch = (over: Partial<LaborDraft>) => {
    setDraft((was) => (was ? { ...was, ...over } : was))
    setErrors((was) => Object.fromEntries(Object.entries(was).filter(([key]) => !(key in over))))
  }

  const person = unitNamed(lookups.units, draft?.purchaseUnitId ?? null, 'Person hour')
  const crew = unitNamed(lookups.units, draft?.unitId ?? null, 'Crew hour')
  const crewSize = Math.max(1, Math.round(readAmount(draft?.crewSize) ?? 1))
  const perPerson = readAmount(draft?.personHourCost) ?? 0
  const cost = perPerson * crewSize
  const sells = draft?.pricing === 'markup'
    ? Math.round(cost * (1 + (readAmount(draft.markupPct) ?? 0) / 100) * 100) / 100
    : readAmount(draft?.billedRate) ?? 0
  const margin = marginPctOf(Math.round(sells * 1e6), Math.round(cost * 1e6))
  const crewWord = crew.name.toLowerCase()

  const changes = useMemo(() => countChanges(draft, saved), [draft, saved])

  async function save(again: boolean) {
    if (!draft) return
    const found: Record<string, string> = {}
    if (!draft.name.trim()) found.name = 'A labor rate needs a name'
    if (!draft.unitId) found.unitId = 'Say what a job is charged in'
    setErrors(found)
    if (Object.keys(found).length > 0) {
      body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      await request(id ? `/catalog-admin/labor-rates/${id}` : '/catalog-admin/labor-rates', {
        method: id ? 'PUT' : 'POST',
        body: {
          name: draft.name,
          customerName: draft.customerName.trim() || null,
          description: draft.description,
          purchaseUnitId: draft.purchaseUnitId,
          unitId: draft.unitId,
          personHourCost: perPerson,
          crewSize,
          billedRate: draft.pricing === 'fixed' ? readAmount(draft.billedRate) ?? 0 : null,
          markupPct: draft.pricing === 'markup' ? readAmount(draft.markupPct) ?? 0 : null,
          taxable: draft.taxable,
          orgUnitIds: draft.orgUnitIds,
          notes: draft.notes || null,
          active: draft.active,
          ...(id ? { version: draft.version ?? 0 } : {}),
        },
      })
      if (again) {
        const next = { ...BLANK, purchaseUnitId: draft.purchaseUnitId, unitId: draft.unitId }
        setDraft(next)
        setSaved(next)
      }
      onSaved(id ? `Saved ${draft.name}.` : `Added ${draft.name}.`, again)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const errorCount = Object.keys(errors).length
  return (
    <CatalogSheet
      kind="labor"
      tile="labor"
      title={draft?.name || (id ? 'Labor rate' : 'New labor rate')}
      dirty={changes > 0}
      changeCount={changes}
      busy={busy}
      canEdit={canEdit}
      isNew={!id}
      onClose={onClose}
      onSave={(again) => void save(again)}
      note={errorCount > 0
        ? (errorCount === 1 ? 'One field needs attention' : `${errorCount} fields need attention`)
        : undefined}
    >
      {!draft
        ? <p className="m-0 text-cell text-text-muted">Loading…</p>
        : (
          <div ref={body} className="flex flex-col gap-3.5">
            {problem && <Band tone="amber">{problem}</Band>}

            <FormSection title={WHAT_IT_IS} />
            <FormGrid>
              <Wide>
                <Field label={NAME} required error={errors.name}>
                  {({ id: fieldId, describedBy, invalid }) => (
                    <TextInput id={fieldId} describedBy={describedBy} invalid={invalid}
                      value={draft.name} disabled={!canEdit}
                      onChange={(event) => patch({ name: event.target.value })} />
                  )}
                </Field>
              </Wide>
              <CustomerNameField value={draft.customerName} name={draft.name} disabled={!canEdit}
                onChange={(customerName) => patch({ customerName })} />
              <Wide>
                <Field label={DESCRIPTION} optional>
                  {({ id: fieldId }) => (
                    <TextInput id={fieldId} value={draft.description} disabled={!canEdit}
                      onChange={(event) => patch({ description: event.target.value })} />
                  )}
                </Field>
              </Wide>
            </FormGrid>

            <FormSection title={WHAT_IT_COSTS} sub={WHAT_IT_COSTS_SUB} />
            <PairField
              joiner="at"
              left={(
                <Field label={BUY_LABEL.labor}>
                  {({ id: fieldId }) => (
                    <UnitSelect id={fieldId} label={BUY_LABEL.labor} units={lookups.units}
                      value={draft.purchaseUnitId} disabled={!canEdit}
                      onChange={(value) => patch({ purchaseUnitId: value })}
                      onCreate={canEdit ? (name) => void made(name, 'purchaseUnitId') : undefined} />
                  )}
                </Field>
              )}
              right={(
                <Field label={costPerLabel('labor', person.name)}>
                  {({ id: fieldId }) => (
                    <MoneyInput id={fieldId} value={draft.personHourCost} disabled={!canEdit}
                      onChange={(event) => patch({ personHourCost: event.target.value })} />
                  )}
                </Field>
              )}
            />
            <PairField
              joiner="="
              left={(
                <Field label={ALLOCATED_IN} required error={errors.unitId}>
                  {({ id: fieldId, invalid }) => (
                    <UnitSelect id={fieldId} label={ALLOCATED_IN} units={lookups.units}
                      value={draft.unitId} invalid={invalid} disabled={!canEdit}
                      onChange={(value) => patch({ unitId: value })}
                      onCreate={canEdit ? (name) => void made(name, 'unitId') : undefined} />
                  )}
                </Field>
              )}
              right={(
                <Field label={perLabel(`${person.name.toLowerCase()}s`, crew.name)}>
                  {({ id: fieldId }) => (
                    <NumberInput id={fieldId} unit={PEOPLE} value={draft.crewSize}
                      disabled={!canEdit}
                      onChange={(event) => patch({ crewSize: event.target.value })} />
                  )}
                </Field>
              )}
            />
            <Band tone="green" title={laborSentence(crewSize, perPerson, crew.name, person.name)}>
              {BAND_HINT.labor}
            </Band>

            <FormSection title={WHAT_IT_SELLS_FOR} />
            <PricedByField value={draft.pricing} disabled={!canEdit}
              onChange={(mode) => patch({ pricing: mode === 'company' ? 'markup' : mode })} />
            <FormGrid>
              {draft.pricing === 'fixed'
                ? (
                  <Field label={pricePer(crewWord)}>
                    {({ id: fieldId }) => (
                      <MoneyInput id={fieldId} value={draft.billedRate} disabled={!canEdit}
                        onChange={(event) => patch({ billedRate: event.target.value })} />
                    )}
                  </Field>
                )
                : (
                  <Field label={MARKUP_ON_COST}>
                    {({ id: fieldId }) => (
                      <NumberInput id={fieldId} percent value={draft.markupPct} disabled={!canEdit}
                        onChange={(event) => patch({ markupPct: event.target.value })} />
                    )}
                  </Field>
                )}
            </FormGrid>
            <Readout items={[
              { label: costSlash(crewWord), value: formatRate(cost) },
              { label: sellsSlash(crewWord), value: formatRate(sells) },
              { label: 'Margin', value: percentText(margin), tone: marginTone(margin) },
            ]} />

            <WhereAndOptions available={available} orgUnitIds={draft.orgUnitIds}
              onOrgUnits={(next) => patch({ orgUnitIds: next })} taxable={draft.taxable}
              onTaxable={(next) => patch({ taxable: next })} disabled={!canEdit} />
            <NotesField value={draft.notes} disabled={!canEdit}
              onChange={(next) => patch({ notes: next })} />
          </div>
        )}
    </CatalogSheet>
  )

  async function made(name: string, key: 'purchaseUnitId' | 'unitId') {
    const unit = await createUnit(name, lookups, setProblem)
    if (unit) patch({ [key]: unit.id } as Partial<LaborDraft>)
  }
}

/** How many keys differ from what was loaded — the close-without-saving count. */
export function countChanges<T extends object>(now: T | null, was: T | null): number {
  if (!now || !was) return 0
  return Object.keys(now).filter((key) =>
    JSON.stringify((now as Record<string, unknown>)[key])
      !== JSON.stringify((was as Record<string, unknown>)[key])).length
}

export type { UnitOption }
